import jsPDF from "jspdf";
import "jspdf-autotable";
import { dateTime, money, num, isStale } from "./format";
import New_LOGO from "../../../../../../../assets/utils/images/New_LOGO.png";

/**
 * Official Simpliworks Brand Palette
 * Matches designUtils/Colors.js and the SellerOS UI theme.
 */
const BRAND = {
  navy: [5, 40, 71], // #052847 - simpliBlueDarker
  blueDark: [17, 71, 120], // #114778 - simpliBlueDark
  blue: [61, 133, 198], // #3D85C6 - simpliBlue
  green: [135, 208, 81], // #87D051 - simpliGreen
  darkGray: [47, 46, 46], // #2F2E2E - darkGray
  beige: [238, 245, 243], // #EEF5F3 - beige soft
  crit: [214, 69, 65], // #D64541 - urgent red
  warn: [227, 148, 15], // #E3940F - warning amber
  ok: [76, 175, 47], // #4CAF2F - success green
  slateText: [30, 41, 59], // #1E293B
  mutedText: [100, 116, 139], // #64748B
  border: [226, 232, 240], // #E2E8F0
};

/**
 * Helper to call autoTable safely across various bundlers.
 */
function callAutoTable(doc, options) {
  if (typeof doc.autoTable === "function") {
    return doc.autoTable(options);
  }
  const autoTableFn = jsPDF.API ? jsPDF.API.autoTable : null;
  if (typeof autoTableFn === "function") {
    return autoTableFn.call(doc, options);
  }
}

/**
 * Safely extracts ending Y position from autoTable with fallback.
 */
function getFinalY(doc, fallback) {
  return doc.lastAutoTable && typeof doc.lastAutoTable.finalY === "number"
    ? doc.lastAutoTable.finalY
    : fallback;
}

/**
 * Checks if current Y position has enough room for next section; if not, adds a new page.
 */
function ensureSpace(doc, currentY, neededSpace = 35) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + neededSpace > pageHeight - 20) {
    doc.addPage();
    return 28; // reset to top margin below mini header
  }
  return currentY;
}

/**
 * Adds a section title with Simpliworks brand accent bar.
 */
function addSectionHeader(doc, title, y, subtext = null) {
  // Brand accent pill on the left
  doc.setFillColor(BRAND.blue[0], BRAND.blue[1], BRAND.blue[2]);
  doc.roundedRect(14, y - 3, 2.5, 7.5, 0.5, 0.5, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(BRAND.navy[0], BRAND.navy[1], BRAND.navy[2]);
  doc.text(title, 19, y + 2.5);
  // Measure title width while font is 10.5pt bold to prevent subtext overlap
  const titleWidth = doc.getTextWidth(title);

  if (subtext) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(BRAND.mutedText[0], BRAND.mutedText[1], BRAND.mutedText[2]);
    const cleanSubtext = subtext.startsWith("(") ? subtext : `(${subtext})`;
    doc.text(cleanSubtext, 19 + titleWidth + 4, y + 2.5);
  }
  return y + 6;
}

/**
 * Generates and downloads a branded Seller Report PDF featuring official Simpliworks
 * logo, brand colour scheme (SimpliBlue #3D85C6, SimpliGreen #87D051, Navy #052847),
 * and prioritized seller-facing sections.
 *
 * @param {Object} data - The complete inventory data object from useInventoryAlerts
 * @param {Object} options - Configuration options:
 *   - sellerName: string (store/seller name)
 *   - periodText: string (selected period, e.g. "This week (15–21 Oct)")
 *   - sections: { actToday, dueThisWeek, inventoryHealth, inbound, moneyRecovery, doneForYou, summary }
 *   - currency: string (e.g. 'USD')
 */
export async function generateInventoryPdf(data, options = {}) {
  if (!data) return;

  const {
    sellerName = (data.store && data.store.name) || "Seller",
    periodText = "This week",
    sections = {
      actToday: true,
      dueThisWeek: true,
      inventoryHealth: true,
      inbound: true,
      moneyRecovery: true,
      doneForYou: true,
      summary: true,
    },
    currency = (data.inventoryHealth && data.inventoryHealth.rows[0] && data.inventoryHealth.rows[0].currency) || "USD",
  } = options;

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ─────────────────────────────────────────────────────────────
  // 1. PAGE HEADER (Simpliworks Branded Card with Official Logo)
  // ─────────────────────────────────────────────────────────────
  const cardX = 14;
  const cardY = 10;
  const cardW = pageWidth - 28;
  const cardH = 28;

  // Background card with subtle light gray border
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(BRAND.border[0], BRAND.border[1], BRAND.border[2]);
  doc.setLineWidth(0.4);
  doc.roundedRect(cardX, cardY, cardW, cardH, 2.5, 2.5, "FD");

  // Top Accent Stripe: 70% Simpliworks Blue (#3D85C6), 30% Simpliworks Green (#87D051)
  const blueW = cardW * 0.72;
  const greenW = cardW - blueW;
  doc.setFillColor(BRAND.blue[0], BRAND.blue[1], BRAND.blue[2]);
  doc.rect(cardX, cardY, blueW, 2.5, "F");
  doc.setFillColor(BRAND.green[0], BRAND.green[1], BRAND.green[2]);
  doc.rect(cardX + blueW, cardY, greenW, 2.5, "F");

  // Official Simpliworks Logo (Top-Left)
  try {
    doc.addImage(New_LOGO, "PNG", cardX + 5, cardY + 7, 46, 12);
  } catch (err) {
    // Elegant fallback if image is unavailable
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(BRAND.navy[0], BRAND.navy[1], BRAND.navy[2]);
    const logoText = "simpliworks";
    doc.text(logoText, cardX + 6, cardY + 16);
    const logoW = doc.getTextWidth(logoText);
    doc.setTextColor(BRAND.green[0], BRAND.green[1], BRAND.green[2]);
    doc.setFontSize(9);
    doc.text("™", cardX + 6 + logoW + 1, cardY + 11);
  }

  // Right Side Info: Title, Seller Name, Period, Generation Time
  const rightX = cardX + cardW - 6;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(BRAND.navy[0], BRAND.navy[1], BRAND.navy[2]);
  doc.text("SELLER INVENTORY REPORT", rightX, cardY + 9, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(BRAND.blueDark[0], BRAND.blueDark[1], BRAND.blueDark[2]);
  doc.text(`Seller: ${sellerName}`, rightX, cardY + 15, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(BRAND.blue[0], BRAND.blue[1], BRAND.blue[2]);
  doc.text(`Period: ${periodText}`, rightX, cardY + 20, { align: "right" });

  doc.setFontSize(7);
  doc.setTextColor(BRAND.mutedText[0], BRAND.mutedText[1], BRAND.mutedText[2]);
  const genStr = `Generated: ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}`;
  doc.text(genStr, rightX, cardY + 24.5, { align: "right" });

  let currentY = 42;

  // ─────────────────────────────────────────────────────────────
  // 2. TABLE OF CONTENTS RESERVATION (Rendered on Page 1 Above Metrics)
  // ─────────────────────────────────────────────────────────────
  const tocEntries = [];
  if (sections.summary !== false) {
    tocEntries.push({ key: "summary", title: "Key Metrics Overview", page: 1 });
  }
  let sectionIdx = 1;
  if (sections.actToday !== false && data.actionCenter) {
    tocEntries.push({ key: "actToday", title: `${sectionIdx++}. Act Today (Urgent Attention Required)` });
  }
  if (sections.dueThisWeek && data.actionCenter) {
    tocEntries.push({ key: "dueThisWeek", title: `${sectionIdx++}. Due This Week` });
  }
  if (sections.inventoryHealth && data.inventoryHealth) {
    tocEntries.push({ key: "inventoryHealth", title: `${sectionIdx++}. Inventory Health & Restock Recommendations` });
  }
  if (sections.inbound && data.inbound) {
    tocEntries.push({ key: "inbound", title: `${sectionIdx++}. Inbound Shipments (FBA & AWD)` });
  }
  if (sections.moneyRecovery && data.moneyRecovery) {
    tocEntries.push({ key: "moneyRecovery", title: `${sectionIdx++}. Money Recovery (Reimbursement Claims)` });
  }
  if (sections.doneForYou) {
    tocEntries.push({ key: "doneForYou", title: `${sectionIdx++}. Done For You & Next Steps Needed` });
  }

  const tocBoxX = 14;
  const tocBoxY = currentY;
  const tocBoxW = pageWidth - 28;
  const tocBoxH = 10 + tocEntries.length * 5.2;
  currentY += tocBoxH + 6;

  // ─────────────────────────────────────────────────────────────
  // 3. KEY METRICS OVERVIEW (Simpliworks Color Scheme)
  // ─────────────────────────────────────────────────────────────
  if (sections.summary !== false) {
    currentY = addSectionHeader(doc, "Key Metrics Overview", currentY);

    const actKpis = (data.actionCenter && data.actionCenter.kpis) || {};
    const invKpis = (data.inventoryHealth && data.inventoryHealth.kpis) || {};
    const recKpis = (data.moneyRecovery && data.moneyRecovery.kpis) || {};

    const kpiRows = [
      [
        { content: `Act Today\n${actKpis.actToday || 0}`, styles: { fontStyle: "bold", textColor: actKpis.actToday ? BRAND.crit : BRAND.slateText } },
        { content: `Due This Week\n${actKpis.dueThisWeek || 0}`, styles: { fontStyle: "bold", textColor: actKpis.dueThisWeek ? BRAND.warn : BRAND.slateText } },
        { content: `At Risk If Missed\n${money(actKpis.atRiskValue, currency)}`, styles: { fontStyle: "bold", textColor: BRAND.blueDark } },
        { content: `Stockout Before Restock\n${invKpis.stockoutBeforeRestock || 0} SKUs`, styles: { fontStyle: "bold", textColor: invKpis.stockoutBeforeRestock ? BRAND.crit : BRAND.slateText } },
      ],
      [
        { content: `Stockout Risk\n${invKpis.stockoutRisk || 0} SKUs`, styles: { fontStyle: "bold", textColor: invKpis.stockoutRisk ? BRAND.warn : BRAND.slateText } },
        { content: `Aged Surcharge\n${money(invKpis.agedSurchargeIfNoAction, currency)}`, styles: { fontStyle: "bold", textColor: BRAND.darkGray } },
        { content: `Units Can't Sell (Stranded)\n${num(invKpis.unitsCantSell)} units`, styles: { fontStyle: "bold", textColor: BRAND.navy } },
        { content: `Claims Window Closing\n${(recKpis.windowClosingSoon && recKpis.windowClosingSoon.claims) || 0} claims (${money(recKpis.windowClosingSoon && recKpis.windowClosingSoon.value, currency)})`, styles: { fontStyle: "bold", textColor: (recKpis.windowClosingSoon && recKpis.windowClosingSoon.claims) ? BRAND.crit : BRAND.slateText } },
      ],
    ];

    callAutoTable(doc, {
      body: kpiRows,
      startY: currentY,
      margin: { left: 14, right: 14 },
      theme: "grid",
      styles: {
        fontSize: 7.8,
        cellPadding: 3.5,
        halign: "center",
        valign: "middle",
        lineColor: BRAND.border,
        lineWidth: 0.4,
      },
      bodyStyles: {
        fillColor: [248, 250, 252],
      },
    });

    currentY = getFinalY(doc, currentY + 25) + 7;
  }

  // Executive Operational Scope Card on Page 1 (keeps Page 1 complete and prevents tables crammed in middle)
  const guideBoxX = 14;
  const guideBoxY = currentY;
  const guideBoxW = pageWidth - 28;
  const guideBoxH = 46;

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(BRAND.border[0], BRAND.border[1], BRAND.border[2]);
  doc.setLineWidth(0.3);
  doc.roundedRect(guideBoxX, guideBoxY, guideBoxW, guideBoxH, 2, 2, "FD");

  doc.setFillColor(BRAND.green[0], BRAND.green[1], BRAND.green[2]);
  doc.rect(guideBoxX, guideBoxY, 2.5, guideBoxH, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(BRAND.navy[0], BRAND.navy[1], BRAND.navy[2]);
  doc.text("REPORT STRUCTURE & OPERATIONAL GUIDANCE", guideBoxX + 6, guideBoxY + 5.5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.2);
  doc.setTextColor(BRAND.slateText[0], BRAND.slateText[1], BRAND.slateText[2]);

  const guidePoints = [
    "• Immediate Action: Act Today & Due This Week prioritize urgent decisions to prevent stockouts and aged surcharges.",
    "• Supply & Health: Detailed SKU stock cover, replenishment deadlines, and inbound shipment milestones across FBA and AWD.",
    "• Capital Recovery: Open Amazon reimbursement claims tracked with closing claim filing deadlines to maximize recovered revenue.",
    "• Interactive Navigation: Click any section above to jump directly to it. Click 'Back to Contents' in the footer to return.",
  ];

  const maxGuideW = guideBoxW - 12;
  let pointY = guideBoxY + 11.5;
  guidePoints.forEach((pt) => {
    const wrapped = doc.splitTextToSize(pt, maxGuideW);
    doc.text(wrapped, guideBoxX + 6, pointY);
    pointY += wrapped.length * 4.2 + 1.2;
  });

  doc.setFont("helvetica", "italic");
  doc.setFontSize(6.8);
  doc.setTextColor(BRAND.mutedText[0], BRAND.mutedText[1], BRAND.mutedText[2]);
  doc.text("Simpliworks SellerOS  •  Inventory Intelligence & Revenue Operations", guideBoxX + 6, guideBoxY + guideBoxH - 4);

  const recordSectionPage = (key) => {
    const item = tocEntries.find((e) => e.key === key);
    if (item) {
      item.page = doc.internal.getNumberOfPages();
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 3. ACT TODAY (Red actions first — always included)
  // ─────────────────────────────────────────────────────────────
  if (sections.actToday !== false && data.actionCenter) {
    doc.addPage();
    recordSectionPage("actToday");
    let secY = 16;
    const actEntry = tocEntries.find((e) => e.key === "actToday");
    const actToday = (data.actionCenter.actToday || []).filter(
      (a) => a.target && a.target.view !== "health" // Exclude internal system health
    );

    secY = addSectionHeader(
      doc,
      (actEntry && actEntry.title) || "Act Today (Urgent Attention Required)",
      secY,
      `${actToday.length} item${actToday.length === 1 ? "" : "s"}`
    );

    if (actToday.length === 0) {
      callAutoTable(doc, {
        head: [["Status", "Action Item", "Reason & Impact", "Category"]],
        body: [["All Clear", "No urgent actions requiring immediate attention today.", "All inventory metrics within normal operational thresholds.", "-"]],
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.navy, textColor: [255, 255, 255], fontSize: 8 },
        styles: { fontSize: 7.5, cellPadding: 3 },
      });
    } else {
      const rows = actToday.map((a) => [
        { content: a.due || "Today", styles: { textColor: BRAND.crit, fontStyle: "bold" } },
        a.title || "—",
        a.why || "—",
        (a.tags || []).map((t) => t.label).join(", ") || (a.target && a.target.view) || "URGENT",
      ]);

      callAutoTable(doc, {
        head: [["Due", "Action Item", "Reason / Impact", "Tags"]],
        body: rows,
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.crit, textColor: [255, 255, 255], fontSize: 8 },
        alternateRowStyles: { fillColor: [254, 242, 242] },
        styles: { fontSize: 7.5, cellPadding: 3, overflow: "linebreak" },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 65 },
          2: { cellWidth: 70 },
          3: { cellWidth: 27 },
        },
      });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 4. DUE THIS WEEK
  // ─────────────────────────────────────────────────────────────
  if (sections.dueThisWeek && data.actionCenter) {
    doc.addPage();
    recordSectionPage("dueThisWeek");
    let secY = 16;
    const dueEntry = tocEntries.find((e) => e.key === "dueThisWeek");
    const thisWeek = (data.actionCenter.dueThisWeek || []).filter(
      (a) => a.target && a.target.view !== "health"
    );

    secY = addSectionHeader(
      doc,
      (dueEntry && dueEntry.title) || "Due This Week",
      secY,
      `${thisWeek.length} item${thisWeek.length === 1 ? "" : "s"}`
    );

    if (thisWeek.length === 0) {
      callAutoTable(doc, {
        head: [["Status", "Action Item", "Reason & Impact", "Category"]],
        body: [["All Clear", "No upcoming action items scheduled for this week.", "Operations running smoothly.", "-"]],
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.navy, textColor: [255, 255, 255], fontSize: 8 },
        styles: { fontSize: 7.5, cellPadding: 3 },
      });
    } else {
      const rows = thisWeek.map((a) => [
        { content: a.due || "This week", styles: { textColor: BRAND.warn, fontStyle: "bold" } },
        a.title || "—",
        a.why || "—",
        (a.tags || []).map((t) => t.label).join(", ") || (a.target && a.target.view) || "THIS WEEK",
      ]);

      callAutoTable(doc, {
        head: [["Due", "Action Item", "Reason / Impact", "Tags"]],
        body: rows,
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.warn, textColor: [255, 255, 255], fontSize: 8 },
        alternateRowStyles: { fillColor: [255, 251, 235] },
        styles: { fontSize: 7.5, cellPadding: 3, overflow: "linebreak" },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 65 },
          2: { cellWidth: 70 },
          3: { cellWidth: 27 },
        },
      });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 5. INVENTORY HEALTH
  // ─────────────────────────────────────────────────────────────
  if (sections.inventoryHealth && data.inventoryHealth) {
    doc.addPage();
    recordSectionPage("inventoryHealth");
    let secY = 16;
    const invEntry = tocEntries.find((e) => e.key === "inventoryHealth");
    const rowsSource = data.inventoryHealth.rows || [];

    secY = addSectionHeader(
      doc,
      (invEntry && invEntry.title) || "Inventory Health & Restock Recommendations",
      secY,
      `${rowsSource.length} active SKUs`
    );

    if (rowsSource.length === 0) {
      callAutoTable(doc, {
        head: [["SKU", "ASIN", "Product Title", "Cover", "Needs", "FBA Stock", "Status"]],
        body: [["—", "—", "No inventory records available.", "—", "—", "—", "OK"]],
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.blueDark, textColor: [255, 255, 255], fontSize: 8 },
        styles: { fontSize: 7.5, cellPadding: 3 },
      });
    } else {
      const tableRows = rowsSource.map((r) => {
        const coverStr = r.coverDays != null ? `${Math.round(r.coverDays)} d` : "No sales";
        const needsStr = r.needsDays != null ? `${Math.round(r.needsDays)} d` : "—";
        const isCrit = r.alert && r.alert.level === "crit";
        const isWarn = r.alert && r.alert.level === "warn";
        const alertLabel = (r.alert && r.alert.label) || (r.actToday ? "Act Today" : (isCrit ? "Stockout Before Restock" : (isWarn ? "Stockout Risk" : "Normal")));

        return [
          r.sku || "—",
          r.asin || "—",
          ((r.name || "").length > 40 ? `${(r.name || "").slice(0, 38)}…` : r.name) || "—",
          coverStr,
          needsStr,
          num(r.fba != null ? r.fba : (r.numbers && r.numbers.fbaAvailable)),
          num(r.inbound != null ? r.inbound : (r.numbers && (r.numbers.inboundToFba || r.numbers.inboundWorking))),
          {
            content: alertLabel,
            styles: {
              textColor: isCrit ? BRAND.crit : isWarn ? BRAND.warn : BRAND.ok,
              fontStyle: isCrit || isWarn ? "bold" : "normal",
            },
          },
        ];
      });

      callAutoTable(doc, {
        head: [["SKU", "ASIN", "Product Title", "Cover", "Needs", "FBA Avail", "Inbound", "Status"]],
        body: tableRows,
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.blueDark, textColor: [255, 255, 255], fontSize: 8 },
        alternateRowStyles: { fillColor: [240, 246, 250] },
        styles: { fontSize: 7.5, cellPadding: 3, overflow: "linebreak" },
        columnStyles: {
          0: { cellWidth: 26 },
          1: { cellWidth: 22 },
          2: { cellWidth: 54 },
          3: { cellWidth: 15, halign: "center" },
          4: { cellWidth: 15, halign: "center" },
          5: { cellWidth: 16, halign: "right" },
          6: { cellWidth: 16, halign: "right" },
          7: { cellWidth: 22 },
        },
      });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 6. INBOUND SHIPMENTS
  // ─────────────────────────────────────────────────────────────
  if (sections.inbound && data.inbound) {
    doc.addPage();
    recordSectionPage("inbound");
    let secY = 16;
    const inbEntry = tocEntries.find((e) => e.key === "inbound");

    secY = addSectionHeader(
      doc,
      (inbEntry && inbEntry.title) || "Inbound Shipments (FBA & AWD)",
      secY
    );

    const cols = data.inbound.columns || [];
    let inboundItems = [];
    cols.forEach((col) => {
      (col.items || []).forEach((item) => {
        inboundItems.push({ ...item, stage: col.title });
      });
    });

    if (inboundItems.length === 0) {
      callAutoTable(doc, {
        head: [["Stage", "Shipment / Plan ID", "Network", "Units", "Alert / Due Status"]],
        body: [["—", "No shipments currently in transit or receiving.", "—", "—", "On schedule"]],
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.blueDark, textColor: [255, 255, 255], fontSize: 8 },
        styles: { fontSize: 7.5, cellPadding: 3 },
      });
    } else {
      const rows = inboundItems.map((item) => {
        const isUrgent = item.level === "crit";
        const isWarn = item.level === "warn";
        return [
          item.stage || "—",
          item.id || item.shipmentId || item.planId || "—",
          item.network || "FBA",
          num(item.units || (item.data && item.data.units)),
          {
            content: item.due || (isUrgent ? "Action Needed" : (isWarn ? "Attention Needed" : "On Track")),
            styles: {
              textColor: isUrgent ? BRAND.crit : isWarn ? BRAND.warn : BRAND.slateText,
              fontStyle: isUrgent || isWarn ? "bold" : "normal",
            },
          },
        ];
      });

      callAutoTable(doc, {
        head: [["Stage", "Shipment / Plan ID", "Network", "Units", "Alert / Due Status"]],
        body: rows,
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.blueDark, textColor: [255, 255, 255], fontSize: 8 },
        alternateRowStyles: { fillColor: [240, 246, 250] },
        styles: { fontSize: 7.5, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 50 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25, halign: "right" },
          4: { cellWidth: 52 },
        },
      });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 7. MONEY RECOVERY (CLAIMS)
  // ─────────────────────────────────────────────────────────────
  if (sections.moneyRecovery && data.moneyRecovery) {
    doc.addPage();
    recordSectionPage("moneyRecovery");
    let secY = 16;
    const recEntry = tocEntries.find((e) => e.key === "moneyRecovery");

    secY = addSectionHeader(
      doc,
      (recEntry && recEntry.title) || "Money Recovery (Reimbursement Claims)",
      secY
    );

    const claims = data.moneyRecovery.claims || [];

    if (claims.length === 0) {
      callAutoTable(doc, {
        head: [["Claim Title", "SKU", "Units", "Value", "Claim Window", "Stage", "Next Step"]],
        body: [["No claims open.", "—", "—", "—", "—", "—", "All inventory losses reconciled."]],
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.blueDark, textColor: [255, 255, 255], fontSize: 8 },
        styles: { fontSize: 7.5, cellPadding: 3 },
      });
    } else {
      const rows = claims.map((c) => {
        const isUrgent = c.level === "u";
        return [
          c.title || c.id || "—",
          c.sku || "—",
          num(c.units),
          money(c.value, currency),
          { content: c.claimWindow || "—", styles: { textColor: isUrgent ? BRAND.crit : BRAND.slateText, fontStyle: isUrgent ? "bold" : "normal" } },
          c.stage || "—",
          c.nextStep || "—",
        ];
      });

      callAutoTable(doc, {
        head: [["Claim Title", "SKU", "Units", "Value", "Claim Window", "Stage", "Next Step"]],
        body: rows,
        startY: secY,
        margin: { left: 14, right: 14, top: 16, bottom: 14 },
        theme: "striped",
        headStyles: { fillColor: BRAND.blueDark, textColor: [255, 255, 255], fontSize: 8 },
        alternateRowStyles: { fillColor: [240, 246, 250] },
        styles: { fontSize: 7.5, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 25 },
          2: { cellWidth: 15, halign: "right" },
          3: { cellWidth: 20, halign: "right" },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 32 },
        },
      });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 8. DONE FOR YOU AND WHAT WE NEED FROM YOU
  // ─────────────────────────────────────────────────────────────
  if (sections.doneForYou) {
    doc.addPage();
    recordSectionPage("doneForYou");
    let secY = 16;
    const doneEntry = tocEntries.find((e) => e.key === "doneForYou");

    secY = addSectionHeader(
      doc,
      (doneEntry && doneEntry.title) || "Done For You & Next Steps Needed",
      secY
    );

    const doneItems = [
      "• Automated reconciliation of inventory losses across Amazon fulfillment centers.",
      "• Tracked replenishment deadlines and days of cover against supplier lead times.",
      "• Monitored FBA and AWD inbound shipments to flag receiving delays.",
    ].join("\n");

    const needItems = [
      "• Restock authorization: Approve orders for SKUs identified under 'Act today'.",
      "• Supplier proof of value: Submit invoices for open reimbursement claims closing soon.",
      "• Shipment carrier confirmation: Provide tracking for shipments awaiting check-in.",
    ].join("\n");

    callAutoTable(doc, {
      head: [["Done for you by Simpliworks", "What we need from you"]],
      body: [[doneItems, needItems]],
      startY: secY,
      margin: { left: 14, right: 14, top: 16, bottom: 14 },
      theme: "grid",
      headStyles: { fillColor: BRAND.navy, textColor: [255, 255, 255], fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      styles: { fontSize: 7.5, cellPadding: 4, overflow: "linebreak", lineColor: BRAND.border, lineWidth: 0.3 },
      columnStyles: {
        0: { cellWidth: 91 },
        1: { cellWidth: 91 },
      },
    });
  }

  // ─────────────────────────────────────────────────────────────
  // 9. TABLE OF CONTENTS DRAWING (Page 1 with Clickable Links)
  // ─────────────────────────────────────────────────────────────
  if (tocEntries.length > 0) {
    doc.setPage(1);

    // Card background for TOC
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(BRAND.border[0], BRAND.border[1], BRAND.border[2]);
    doc.setLineWidth(0.4);
    doc.roundedRect(tocBoxX, tocBoxY, tocBoxW, tocBoxH, 2, 2, "FD");

    // Left vertical accent stripe
    doc.setFillColor(BRAND.blue[0], BRAND.blue[1], BRAND.blue[2]);
    doc.rect(tocBoxX, tocBoxY, 2.5, tocBoxH, "F");

    // TOC Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(BRAND.navy[0], BRAND.navy[1], BRAND.navy[2]);
    const tocTitle = "TABLE OF CONTENTS";
    doc.text(tocTitle, tocBoxX + 6, tocBoxY + 5.5);
    // Measure while font is 8.5pt bold to ensure subtitle starts cleanly after title
    const tocTitleW = doc.getTextWidth(tocTitle);

    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(BRAND.mutedText[0], BRAND.mutedText[1], BRAND.mutedText[2]);
    doc.text("(Click any section below to jump directly to its start)", tocBoxX + 6 + tocTitleW + 4, tocBoxY + 5.5);

    // Render each TOC row with clickable link
    tocEntries.forEach((entry, idx) => {
      const rowY = tocBoxY + 10.5 + idx * 5.2;
      const targetPage = entry.page || 1;
      const pageLabel = `Page ${targetPage}`;

      // Section title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(BRAND.blueDark[0], BRAND.blueDark[1], BRAND.blueDark[2]);
      const titleX = tocBoxX + 6;
      doc.text(entry.title, titleX, rowY);
      const titleW = doc.getTextWidth(entry.title);

      // Page badge on right
      const badgeX = tocBoxX + tocBoxW - 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(BRAND.blue[0], BRAND.blue[1], BRAND.blue[2]);
      doc.text(pageLabel, badgeX, rowY, { align: "right" });
      const pageLabelW = doc.getTextWidth(pageLabel);

      // Dotted leader lines connecting title and page
      const startDots = titleX + titleW + 3;
      const endDots = badgeX - pageLabelW - 3;
      if (endDots > startDots + 4) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        doc.setTextColor(203, 213, 225); // Slate-300
        const dotW = doc.getTextWidth(". ");
        const dotCount = Math.floor((endDots - startDots) / dotW);
        if (dotCount > 0) {
          doc.text(". ".repeat(dotCount), startDots, rowY);
        }
      }

      // Clickable link hotspot over the whole row — scrolls to top: 0 (the initial beginning of the section)
      doc.link(tocBoxX, rowY - 3.5, tocBoxW, 5.2, { pageNumber: targetPage, top: 0 });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // 10. GLOBAL FOOTERS (Page X of Y & back-to-contents link)
  // ─────────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Clean footer text
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(BRAND.mutedText[0], BRAND.mutedText[1], BRAND.mutedText[2]);
    doc.text(`Simpliworks SellerOS  |  Seller Report: ${sellerName}  (${periodText})`, 14, pageHeight - 8);

    const pageStr = `Page ${i} of ${totalPages}`;
    const pageStrW = doc.getTextWidth(pageStr); // Measure while font is 7.5pt normal
    doc.text(pageStr, pageWidth - 14, pageHeight - 8, { align: "right" });

    // Clickable "Back to Contents" link on pages 2+
    if (i > 1) {
      const tocLink = "Back to Contents";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(BRAND.blue[0], BRAND.blue[1], BRAND.blue[2]);
      const tocLinkW = doc.getTextWidth(tocLink); // Measure while font is 7pt bold
      const linkX = pageWidth - 14 - pageStrW - tocLinkW - 6;
      doc.text(tocLink, linkX, pageHeight - 8);
      doc.link(linkX - 1, pageHeight - 11, tocLinkW + 2, 4.5, { pageNumber: 1, top: 0 });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 10. SAVE FILE
  // ─────────────────────────────────────────────────────────────
  const safeSeller = sellerName.replace(/[^a-zA-Z0-9_-]/g, "_").replace(/_+/g, "_");
  const dateTag = new Date().toISOString().slice(0, 10);
  const fileName = `Simpliworks_Seller_Report_${safeSeller}_${dateTag}.pdf`;
  doc.save(fileName);
}
